import { IErrorStore } from "./types";
import {
  validationRules,
  validationMessages,
  ValidationRule,
  ValidatableValue,
  validateDynamicRule,
} from "./validationRules";

/**
 * Creates a `validateField` function bound to a specific error store and field validation map.
 */
export function createFieldValidator<
  FieldKey extends string,
  FlowSlug extends string,
>(
  errorStore: IErrorStore<FieldKey, FlowSlug>,
  fieldValidations: Record<string, ValidationRule[]>,
) {
  return (key: FieldKey, value: ValidatableValue, slug: FlowSlug): boolean => {
    const { setError, clearError } = errorStore;
    const { rules, resolvedKey } = resolveFieldRules(key, fieldValidations);

    if (rules.length === 0) {
      clearError(key, slug);
      return true;
    }

    for (const rule of rules) {
      let isValid = true;
      let errorMsg: string | undefined;
      if (typeof rule === "string") {
        isValid = validationRules[rule](value);
        errorMsg = isValid
          ? undefined
          : validationMessages[rule](resolvedKey);
      } else {
        const error = validateDynamicRule(rule, value);
        isValid = !error;
        errorMsg = error ?? undefined;
      }
      if (!isValid) {
        setError(key, slug, errorMsg || "Unknown validation error");
        return false;
      }
    }

    clearError(key, slug);
    return true;
  };
}

function resolveFieldRules<FieldKey extends string>(
  key: FieldKey,
  fieldValidations: Record<string, ValidationRule[]>,
): { rules: ValidationRule[]; resolvedKey: string } {
  if (fieldValidations[key]) {
    return { rules: fieldValidations[key], resolvedKey: key };
  }

  const match = key.match(/^cat\[[^\]]+\]\.(.+)$/);
  if (match) {
    const fallbackKey = `cat.*.${match[1]}`;
    return { rules: fieldValidations[fallbackKey] ?? [], resolvedKey: fallbackKey };
  }

  return { rules: [], resolvedKey: key };
}
